/**
 * Pole SPOLEČNÁ oběma veřejným poptávkám — k zájezdu (`inquirySchema`) i bez zájezdu
 * (`customInquirySchema`).
 *
 * Vzniklo proto, že se ~55 řádků Zod definic v obou schématech shodovalo ZNAK PO ZNAKU
 * včetně českých hlášek: obě plochy je vykreslují doslova pod poli, takže rozejít se
 * ve znění by znamenalo, že týž zákazník dostane za tutéž chybu dvě různé věty. Tady je
 * jediný domov jejich skladby (znění a meze drží zod-free `../inquiryRules`).
 *
 * Bydlí vedle `primitives.ts` a ze stejného důvodu: barel `schemas/index.ts` per-doménové
 * moduly re-exportuje, takže z nich do něj nesmí vést import zpátky.
 *
 * **Skládá se spreadem a na PŮVODNÍM místě v pořadí klíčů.** Pořadí klíčů tvaru určuje
 * pořadí Zod chyb, z něj react-hook-form skládá `errors`, a `firstErrorKey` (web) z něj
 * bere pole, na které skočí fokus — přeházet je by tiše změnilo, kam se zákazník po
 * odeslání dostane.
 */
import { z } from "zod";
import { CLICK_ID_MAX_LENGTH, UTM_MAX_LENGTH } from "../utils";
import {
  CHILD_AGE_MAX,
  CLICK_SOURCE_MAX_LENGTH,
  INQUIRY_MESSAGES as M,
  MAX_ADULTS,
  MAX_CHILDREN,
  MIN_ADULTS,
  NAME_MAX_LENGTH,
  NOTES_MAX_LENGTH,
  ORIGIN_PATH_MAX_LENGTH,
  ORIGIN_PATH_SHAPE,
  PHONE_MAX_LENGTH,
  PHONE_MIN_LENGTH,
  blankToUndefined,
  isFilledChildAge,
} from "../inquiryRules";

// Meze žijí v zod-free `../inquiryRules` (čte je i web); barel nese jména dál odsud.
export { MAX_CHILDREN, NAME_MAX_LENGTH, NOTES_MAX_LENGTH };

/**
 * České hlášky patří i ke STROPŮM, ne jen ke spodním mezím.
 *
 * `.max()` bez zprávy dosadí výchozí ANGLICKÝ text Zodu (`String must contain at most
 * 2000 character(s)`) — a ten se vykreslí doslova pod českým polem: klient ho podává přes
 * resolver formuláře → `InquiryField` → `fieldErrorMessage`, server přes
 * `errorResponse(…, result.error.errors[0].message)`. Znění i meze mají jediný domov
 * v `../inquiryRules`; strop bez hlášky ten slib tiše ruší.
 */

/** Meze telefonu společné všem veřejným formulářům (poptávky i „Zavoláme vám"). */
const phoneBounds = (base: z.ZodString) =>
  base.min(PHONE_MIN_LENGTH, M.phoneRequired).max(PHONE_MAX_LENGTH, M.phoneTooLong);

/** Kdo poptává. Bez kontaktu není lead — proto jediná povinná skupina v obou schématech. */
export const CONTACT_FIELDS = {
  firstName: z.string().min(1, M.firstNameRequired).max(NAME_MAX_LENGTH, M.firstNameTooLong),
  lastName: z.string().min(1, M.lastNameRequired).max(NAME_MAX_LENGTH, M.lastNameTooLong),
  email: z.string().email(M.emailInvalid),
  phone: phoneBounds(z.string()),
} as const;

/**
 * Pole žádosti „Zavoláme vám" (řez 01, F1.9). Na rozdíl od `CONTACT_FIELDS` mají česká
 * hláška i pro CHYBĚJÍCÍ klíč a špatný typ: widget posílá jen dvě pole, takže anglické
 * „Required" z vynechaného klíče by se zákazníkovi ukázalo doslova.
 */
export const CALLBACK_CONTACT_FIELDS = {
  name: z
    .string({
      required_error: M.firstNameRequired,
      invalid_type_error: M.firstNameRequired,
    })
    .trim()
    .min(1, M.firstNameRequired)
    .max(NAME_MAX_LENGTH, M.firstNameTooLong),
  phone: phoneBounds(
    z.string({ required_error: M.phoneRequired, invalid_type_error: M.phoneRequired }).trim(),
  ),
} as const;

/**
 * Veřejná cesta stránky, ze které formulář přišel. Tvar se hlídá už tady, aby se do dat
 * nedostal řetězec, který cesta být nemůže — koncový bod je veřejný a `originPath` se pak
 * vypisuje obchodníkovi v administraci; API hodnotu navíc ověřuje proti route mapě
 * (`isKnownPublicPath`).
 *
 * `.catch(undefined)` je ZÁMĚR, ne benevolence: špatná stránka původu je špatný KONTEXT,
 * ne špatná poptávka. Bez něj by cizí URL v tomhle poli shodila celý požadavek na 400
 * a lead by vůbec nevznikl. Kontrola tvaru zůstává v plné síle, jen její selhání hodnotu
 * ZAHODÍ. Sdílí ho poptávka bez zájezdu i žádost o zavolání.
 */
export const ORIGIN_PATH_FIELD = z.preprocess(
  blankToUndefined,
  z
    .string()
    .max(ORIGIN_PATH_MAX_LENGTH)
    // Tvar (bez protokol-relativní URL) vlastní `ORIGIN_PATH_SHAPE` v `inquiryRules`.
    .regex(ORIGIN_PATH_SHAPE)
    .optional()
    .catch(undefined),
);

/**
 * Kdo jede.
 *
 * Vyprázdněný číselný input posílá `NaN` (`valueAsNumber`), a `z.number()` ho zamítá jako
 * špatný TYP — `.default()` se uplatní jen na chybějící klíč, takže na tuhle cestu nedosáhne.
 * Hlášky proto musí být české: obě poptávkové plochy je vykreslují doslova pod polem.
 * (Klíč chybějící v těle požadavku dál dostane výchozí hodnotu.)
 *
 * `childAges` = věk každého dítěte; ukazuje a vyžaduje se až od `children >= 1` a živí
 * cenotvorbu letenek (infant <2 / child <12). `.max(10)` zrcadlí strop `children` a omezuje
 * pole NEZÁVISLE na `requireChildAges` (ta se při `children = 0` přeskočí) — veřejný koncový
 * bod nesmí přijmout neomezené pole.
 *
 * Preprocess ZAHAZUJE nevyplněná ovládání (`undefined` z rolety hotelového widgetu, `NaN`/""
 * z číselného inputu formuláře) místo toho, aby je nechal spadnout na parsování prvku:
 * selhání prvku ukončí parsování celého objektu, takže by `superRefine` vůbec neproběhl
 * a zákazník by místo české hlášky dostal anglické „Required" na `childAges.0`.
 */
export const TRAVELLER_FIELDS = {
  adults: z
    .number({ invalid_type_error: M.adultsType })
    .int(M.adultsInt)
    .min(MIN_ADULTS, M.adultsMin)
    .max(MAX_ADULTS, M.adultsMax)
    .default(2),
  children: z
    .number({ invalid_type_error: M.childrenType })
    .int(M.childrenInt)
    .min(0, M.childrenMin)
    .max(MAX_CHILDREN, M.childrenMax)
    .default(0),
  childAges: z.preprocess(
    (v) => (Array.isArray(v) ? (v as unknown[]).filter(isFilledChildAge) : v),
    z
      .array(
        z
          .number({ invalid_type_error: M.childAgeType })
          .int(M.childAgeInt)
          .min(0, M.childAgeRange)
          .max(CHILD_AGE_MAX, M.childAgeRange),
      )
      .max(MAX_CHILDREN, M.childAgesTooMany)
      .optional(),
  ),
} as const;

/** Co nám zákazník napsal vlastními slovy. Strop je pro veřejný koncový bod povinný. */
export const NOTES_FIELDS = {
  notes: z.string().max(NOTES_MAX_LENGTH, M.notesTooLong).optional(),
} as const;

/**
 * Odkud návštěvník přišel.
 *
 * Stropy jako u `clickId`: koncový bod je VEŘEJNÝ, takže žádné pole nesmí být neomezené —
 * hodnoty se ukládají do sloupců leadu a vypisují v administraci. Tytéž konstanty ořezávají
 * hodnotu už na webu (`scripts/tracking.ts`), aby dlouhé `utm_campaign` z reklamy neshodilo
 * celé odeslání poptávky.
 *
 * Hláška je společná a mluví jazykem zákazníka: tahle pole žádné viditelné políčko nemají,
 * takže se překročení stropu projeví jedině jako obecná chyba odeslání — a i ta musí být
 * česky, ne `String must contain at most 200 character(s)`.
 */
const ATTRIBUTION_TOO_LONG = M.attributionTooLong;
export const ATTRIBUTION_FIELDS = {
  utmSource: z.string().max(UTM_MAX_LENGTH, ATTRIBUTION_TOO_LONG).optional(),
  utmMedium: z.string().max(UTM_MAX_LENGTH, ATTRIBUTION_TOO_LONG).optional(),
  utmCampaign: z.string().max(UTM_MAX_LENGTH, ATTRIBUTION_TOO_LONG).optional(),
  clickId: z.string().max(CLICK_ID_MAX_LENGTH, ATTRIBUTION_TOO_LONG).optional(),
  clickSource: z.string().max(CLICK_SOURCE_MAX_LENGTH, ATTRIBUTION_TOO_LONG).optional(),
} as const;

/**
 * „Ke každému dítěti jeden věk" — pravidlo `superRefine` sdílené oběma poptávkami.
 * Po preprocessu výš je pole nevyplněných ovládání zbavené, takže je krátké → tahle
 * kontrola délky ho nahlásí česky a na správném poli.
 */
export function requireChildAges(
  ctx: z.RefinementCtx,
  data: { children?: number | null; childAges?: readonly number[] | null },
): void {
  const children = data.children ?? 0;
  if (children < 1) return;
  if ((data.childAges ?? []).length !== children) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: M.childAgesMissing,
      path: ["childAges"],
    });
  }
}
